import HowToRegIcon from '@mui/icons-material/HowToReg';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import ImageListItemBar from '@mui/material/ImageListItemBar';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Tournament } from '../../models/Tournament';
import { getImageUrl } from '../../utils/Url';

interface EnrollmentCardsProps {
  tournaments: Tournament[];
}

export const EnrollmentCards = ({ tournaments }: EnrollmentCardsProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const upMd = useMediaQuery(theme.breakpoints.up('md'));
  const upLg = useMediaQuery(theme.breakpoints.up('lg'));
  const upSm = useMediaQuery(theme.breakpoints.up('sm'));
  const [pressedId, setPressedId] = React.useState<number | null>(null);

  const cols = upLg ? 3 : upMd ? 3 : upSm ? 2 : 1;

  const goToEnrollment = (id: number) => navigate(`/tournament/${id}/enroll`);

  if (!tournaments || tournaments.length === 0) return null;

  return (
    <Box sx={{ width: '90%', mb: 2, mx: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Zapisy otwarte
      </Typography>
      <ImageList cols={cols} gap={12} rowHeight={300} variant="standard">
        {tournaments.map((t) => {
          const src = t.offerUrl ? getImageUrl(t.offerUrl) : '/logo512.png';
          const isPressed = pressedId === t.id;
          return (
            <ImageListItem key={t.id}>
              <Tooltip title={t.offerDetails} arrow>
                <ButtonBase
                  component="div"
                  focusRipple
                  onClick={() => goToEnrollment(t.id)}
                  onMouseDown={() => setPressedId(t.id)}
                  onMouseUp={() => setPressedId(null)}
                  onMouseLeave={() => setPressedId(null)}
                  onTouchStart={() => setPressedId(t.id)}
                  onTouchEnd={() => setPressedId(null)}
                  onTouchCancel={() => setPressedId(null)}
                  onBlur={() => setPressedId(null)}
                  aria-label={`Zapisz się: ${t.name}`}
                  sx={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    textAlign: 'inherit',
                    overflow: 'hidden',
                    borderRadius: 1,
                    cursor: 'pointer',
                    position: 'relative',
                    transition: (theme) =>
                      theme.transitions.create(['transform', 'box-shadow'], { duration: theme.transitions.duration.shortest }),
                    boxShadow: isPressed ? 6 : 1,
                    transform: isPressed ? 'translateY(-2px)' : 'none',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 6,
                    },
                    '&:hover .hoverOverlay': { opacity: 1 },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <Box
                    className="hoverOverlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'action.hover',
                      opacity: isPressed ? 1 : 0,
                      transition: 'opacity 150ms ease',
                      zIndex: 1,
                      pointerEvents: 'none',
                    }}
                  />
                  <img
                    src={src}
                    alt={'Plakat ' + t.name}
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <ImageListItemBar
                    title={t.name}
                    position="bottom"
                    sx={{
                      '& .MuiImageListItemBar-titleWrap': {
                        overflow: 'visible',
                      },
                      '& .MuiImageListItemBar-title': {
                        whiteSpace: 'normal',
                        overflow: 'visible',
                        textOverflow: 'unset',
                        display: 'block',
                        wordBreak: 'break-word',
                        lineHeight: 1.2,
                      },
                    }}
                    subtitle={t.enrollments?.length ? `Zarejestrowano już: ${t.enrollments.length}` : undefined}
                    actionPosition="right"
                    actionIcon={
                      <Tooltip arrow title="Zapisz się">
                        <IconButton
                          aria-label="Zapisz się"
                          size="large"
                          onClick={(e) => {
                            e.stopPropagation();
                            goToEnrollment(t.id);
                          }}
                          sx={{ color: 'white', border: '1px solid', borderColor: 'white', borderRadius: '50%' }}
                        >
                          <HowToRegIcon />
                        </IconButton>
                      </Tooltip>
                    }
                  />
                </ButtonBase>
              </Tooltip>
            </ImageListItem>
          );
        })}
      </ImageList>
    </Box>
  );
};
